import { LEAVE_STATUS, leaveStatusToken } from "./leaveLookups";

/**
 * Leave dates.
 *
 * The API stores a leave as a half-open UTC range: `start_date` included,
 * `end_date` excluded. The UI speaks in whole local days, so a leave picked as
 * "24 – 25 Oct" is sent as local 00:00 on the 24th to local 00:00 on the 26th,
 * and every display path subtracts that exclusive end back off.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Local midnight of a `YYYY-MM-DD` day, or null when unparseable. */
function localMidnight(day) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day || "");
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** A Date as the `YYYY-MM-DD` of its local day. */
export function toDayKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * The API window for a from–to pick, both days inclusive.
 *
 * @param {string} fromDay - `YYYY-MM-DD`.
 * @param {string} toDay - `YYYY-MM-DD`, on or after `fromDay`.
 * @returns {{ start: string, end: string }|null} UTC ISO instants, `end`
 *   exclusive. Null when either day is missing or the range runs backwards.
 */
export function toLeaveWindow(fromDay, toDay) {
  const from = localMidnight(fromDay);
  const to = localMidnight(toDay);
  if (!from || !to || to < from) return null;
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate() + 1);
  return { start: from.toISOString(), end: end.toISOString() };
}

/** First and last local day a leave covers (its end is exclusive). */
export function leaveDayBounds(leave) {
  const start = new Date(leave?.start_date);
  const endExclusive = new Date(leave?.end_date);
  if (Number.isNaN(start.getTime()) || Number.isNaN(endExclusive.getTime())) return null;
  return { first: start, last: new Date(endExclusive.getTime() - 1) };
}

/** Whole days a leave spans (at least 1 for any non-empty range). */
export function leaveDays(leave) {
  const start = new Date(leave?.start_date).getTime();
  const end = new Date(leave?.end_date).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return 0;
  return Math.max(1, Math.round((end - start) / DAY_MS));
}

/** "1 day" / "3 days". */
export function formatDays(n) {
  return `${n} ${n === 1 ? "day" : "days"}`;
}

const dayMonth = (date) => `${date.getDate()} ${MONTHS[date.getMonth()]}`;

/**
 * "24 Oct", "24 – 25 Oct" or "30 Oct – 2 Nov".
 *
 * @param {Object} leave - A leave row.
 * @param {string} [sep=" – "]
 * @returns {string}
 */
export function formatLeaveRange(leave, sep = " – ") {
  const bounds = leaveDayBounds(leave);
  if (!bounds) return "—";
  const { first, last } = bounds;
  if (toDayKey(first) === toDayKey(last)) return dayMonth(first);
  if (first.getFullYear() === last.getFullYear() && first.getMonth() === last.getMonth()) {
    return `${first.getDate()}${sep}${dayMonth(last)}`;
  }
  return `${dayMonth(first)}${sep}${dayMonth(last)}`;
}

/** The date tile on a request row: `{ day: "24", mon: "OCT" }`. */
export function leaveDateTile(leave) {
  const bounds = leaveDayBounds(leave);
  if (!bounds) return { day: "—", mon: "" };
  return {
    day: String(bounds.first.getDate()).padStart(2, "0"),
    mon: MONTHS[bounds.first.getMonth()].toUpperCase(),
  };
}

/**
 * Whether the coach may still withdraw this leave.
 *
 * A pending request can always be withdrawn; an approved one only until it
 * starts. That second rule is the UI's — the API accepts a cancel at any time.
 *
 * @param {Object} leave
 * @param {Date} [now]
 * @returns {boolean}
 */
export function canWithdraw(leave, now = new Date()) {
  const token = leaveStatusToken(leave?.status);
  if (token === LEAVE_STATUS.PENDING) return true;
  if (token !== LEAVE_STATUS.APPROVED) return false;
  const start = new Date(leave?.start_date);
  return !Number.isNaN(start.getTime()) && now < start;
}
